import { useRef, useState, useEffect, memo } from 'react';

interface LazyImageProps {
  src: string | null | undefined;
  alt?: string;
  className?: string;
  placeholderClassName?: string;
  width?: number;
  height?: number;
  rootMargin?: string;
}

const loadedImages = new Set<string>();

const LazyImage = memo(function LazyImage({
  src,
  alt = '',
  className = '',
  placeholderClassName = '',
  width = 20,
  height = 20,
  rootMargin = '100px',
}: LazyImageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(() =>
    src ? loadedImages.has(src) : false,
  );
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) return;

    if (loadedImages.has(src)) {
      setIsLoaded(true);
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [src, rootMargin]);

  const handleLoad = () => {
    if (src) {
      loadedImages.add(src);
    }
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
  };

  if (!src || hasError) {
    return (
      <div
        className={placeholderClassName || className}
        style={{ width, height }}
      />
    );
  }

  return (
    <div ref={ref} className={className} style={{ width, height }}>
      {isVisible && !isLoaded && (
        <div className={placeholderClassName} style={{ width, height }} />
      )}
      {isVisible && (
        <img
          src={src}
          alt={alt}
          className={className}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            display: isLoaded ? 'block' : 'none',
            width,
            height,
          }}
          decoding="async"
        />
      )}
    </div>
  );
});

export default LazyImage;
